/*
 *
 *  * Copyright (c) 2020 General Electric Company. All rights reserved.
 *  *
 *  * The copyright to the computer software herein is the property of
 *  * General Electric Company. The software may be used and/or copied only
 *  * with the written permission of General Electric Company or in accordance
 *  * with the terms and conditions stipulated in the agreement/contract
 *  * under which the software has been supplied.
 *
 */
import * as _ from 'underscore';
import { Injectable } from '@angular/core';
import { Logger } from '@core/lib/services';
import { NotifyService } from '@app/common/notify.service';
import { TranslateService } from '@ngx-translate/core';
import { MessageService } from '@core/lib/services/message.service';
import { WindowRef } from '@app/common/window-ref.service';
import { appConstants } from '@app/common/app-constants';
import { Events, EventType } from '@app/common/event';
import { InvalidArgumentException } from '@core/lib/exceptions';
import { AppConfigService } from '@app/common/app-config.service';
import { UtilService } from '@app/util/util.service';
import { SearchFilterCriteriaService } from '@app/ui/search/search-filter-criteria.service';

const log = new Logger('RealTimeService');

@Injectable({
  providedIn: 'root'
})
export class RealTimeService {
  private webSocket: WebSocket;
  private reConnectTimeout;
  private maxConnectAttempts = 50;
  private reconnectAttempts = 0;

  constructor(
    private translate: TranslateService,
    private notify: NotifyService,
    private messageService: MessageService,
    private winRef: WindowRef,
    private appConfigService: AppConfigService,
    private searchFilterCriteriaService: SearchFilterCriteriaService
  ) {
    this.messageService.on('signInAfterSessionTimeout').subscribe(() => {
      this.init();
    });
  }

  closeSocket() {
    if (this.webSocket && this.webSocket.readyState !== WebSocket.CLOSED) {
      this.webSocket.onclose = null;
      this.webSocket.onopen = null;
      this.webSocket.onmessage = null;
      this.webSocket.onerror = null;
      this.webSocket.close();
    }
  }

  init() {
    const location = this.winRef.nativeWindow.location;
    const url = [
      location.protocol.replace('http', 'ws'),
      '//',
      location.host.replace('4200', '9000'),
      '/idBroadcast?X-Atmosphere-tracking-id=0&' +
        'X-Atmosphere-Framework=2.2.5-javascript&X-Atmosphere-Transport=websocket&' +
        'X-Atmosphere-TrackMessageSize=true&Content-Type=application/json&X-atmo-protocol=true'
    ].join('');
    this.webSocket = new WebSocket(url);
    this.webSocket.onopen = () => {
      this.onOpen();
    };
    this.webSocket.onmessage = e => {
      this.onMessage(e);
    };
    this.webSocket.onerror = () => {
      this.onError();
    };
    this.webSocket.onclose = () => {
      this.onClose();
    };
  }

  private onOpen() {
    if (this.reConnectTimeout) {
      clearTimeout(this.reConnectTimeout);
      this.reconnectAttempts = 0;
      this.displayRealTimeConnectionSuccessThrottle();
    }
  }

  private onError() {
    if (this.webSocket.readyState === WebSocket.OPEN) {
      this.webSocket.close();
    }
  }

  private onClose() {
    this.displayRealTimeConnectionLostThrottle();
    let timeout = 5000;
    if (this.reconnectAttempts === 0) {
      timeout = 0;
    }
    this.reConnectTimeout = setTimeout(() => {
      if (this.reconnectAttempts >= this.maxConnectAttempts) {
        clearTimeout(this.reConnectTimeout);
        log.error('Real-Time connection errored after exhausting ' + this.reconnectAttempts + ' connection retry.');
        this.displayRealTimeConnectionLostThrottle.cancel();
        this.displayRealtimeWarningModal();
        return;
      }
      log.warn('Retried websocket connection ' + this.reconnectAttempts + ' times.');
      this.init();
      this.reconnectAttempts++;
    }, timeout);
  }

  private onMessage(e: any) {
    const responses = RealTimeService.getMessage(e.data);
    if (!_.isUndefined(responses) && !_.isEmpty(responses)) {
      if (Array.isArray(responses)) {
        responses.some(response => {
          if (response.request && response.request.actionCode) {
            this.publishStateEvents(response);
          } else if (RealTimeService.isValidCountResponse(response)) {
            this.messageService.broadcast('WorklistCountChange.' + response.action, response);
          } else if (RealTimeService.isValidTakeControlAutoAckResponse(response)) {
            this.messageService.broadcast('TakeControlAutoAck.' + response.action, response);
          } else if (
            RealTimeService.isValidDicomqrRetrieveResponse(response) ||
            RealTimeService.isValidDicomStatusResponse(response)
          ) {
            this.messageService.broadcast(response.name, response);
          } else if (RealTimeService.isValidExamSplitResponse(response)) {
            const name = response.name != undefined ? response.name : response.event;
            this.messageService.broadcast(name, response);
          } else if (RealTimeService.isValidCacheRefreshResponse(response)) {
            this.messageService.broadcast('CacheRefreshResponse.REFRESH', response);
          } else if (RealTimeService.isValidExamNoteResponse(response)) {
            this.messageService.broadcast('ExamNoteUpdate.' + response.examNoteRequest.action.toString(), response);
          } else if (RealTimeService.isValidExamTagResponse(response)) {
            this.messageService.broadcast('ExamTagUpdate.UPDATE', response);
          } else if (response.name === 'ExamStatusChange') {
            this.messageService.broadcast('ExamStatusChange.UPDATE', response);
          } else if (RealTimeService.isValidPatientHistoryResponse(responses[0])) {
            let _topic;
            if (responses[0].action === 'CLOSE' || responses[0].action === 'ERROR') {
              _topic = responses[0].action;
            } else if (responses[0].requestParameters.primaryExamId[0].indexOf('*') !== -1) {
              _topic =
                responses[0].action + '.FOR.' + responses[0].requestParameters.primaryExamId[0].replace('*', '%');
            } else {
              _topic = responses[0].action + '.FOR.' + responses[0].requestParameters.primaryExamId[0];
            }
            this.messageService.broadcast('COMPARISONS.' + _topic, responses[0]);
          } else if (RealTimeService.isValidStudySearchResponse(responses[0])) {
            const topic = 'STUDY_SEARCH.' + responses[0].action + '.FOR.STUDY_SEARCH_' + responses[0].timeStamp;
            this.messageService.broadcast(topic, responses[0]);
          } else if (RealTimeService.isValidATNARepoCommnResponse(response)) {
            this.messageService.broadcast('ATNARepoCommunicationStatus.' + responses[0].action, responses[0]);
          } else if (RealTimeService.isValidExamPriorityResponse(response)) {
            this.handleExamPriorityUpdateMessage(response);
          } else if (RealTimeService.isValidExamAvailabilityChangeMsgResponse(response)) {
            this.messageService.broadcast('ExamAvailabilityStatusChange.UPDATE', response);
          } else if (RealTimeService.isValidArchiveAdoptedResponse(response[0])) {
            this.messageService.broadcast('ArchiveStatusChange.UPDATE', response[1]);
          } else if (RealTimeService.isValidCacheUnStableResponse(response)) {
            this.messageService.broadcast('CacheUnstableCommunicationStatus.' + responses[0].action, responses[0]);
          } else if (RealTimeService.isValidCacheUnhealthyStatusResponse(response)) {
            this.messageService.broadcast('CacheUnhealthyCommunicationStatus.' + responses[0].action, responses[0]);
          } else if (RealTimeService.isValidCacheHealthyStatusResponse(response)) {
            this.messageService.broadcast('CacheHealthyCommunicationStatus.' + responses[0].action, responses[0]);
          } else if (RealTimeService.isValidWorkListResponse(response)) {
            this.messageService.broadcast(response.name + '.' + response.action, response);
          } else if (RealTimeService.isValidNotificationMsgResponse(response)) {
            this.messageService.broadcast('NotificationMsg.UPDATE', response);
          } else if (RealTimeService.isValidRMQNotificationMsgResponse(response)) {
            this.messageService.broadcast('RMQNotificationMsg.UPDATE', response);
          } else if (RealTimeService.isValidExamFlagResponse(response)) {
            this.messageService.broadcast('ExamFlagChange.UPDATE', response);
          } else if (response.name === 'HMSERVICE') {
            this.messageService.broadcast('HMSERVICE.FAILURE', response);
          } else if (response.name === 'NotificationMsg') {
            this.messageService.broadcast('NotificationMsg.UPDATE', response);
          } else if (response.name === 'ManualFolderExport') {
            this.messageService.broadcast('ManualFolderExport.ADD', response);
          } else if (response.name === 'EditExam') {
            this.messageService.broadcast('examRecord.UPDATE', response.examCkey);
          } else if (response.name === 'EditPatient') {
            this.messageService.broadcast('patientRecord.UPDATE', response.patCkey);
          } else if (response.name === 'ReportSync') {
            this.messageService.broadcast('ReportSync', response);
          } else if (response.name && response.action) {
            this.messageService.broadcast(response.name + '.' + response.action, response);
          } else {
            log.debug('real time update is missed to broadcast, please verify the update', response);
          }
        });
      } else {
        log.error('Unexpected message type received. The message should be an array.', e.data);
      }
    }
  }

  private static getMessage(message: any) {
    const messages = message.substring(message.indexOf(appConstants.messageDelimiter) + 1);
    if (messages) {
      try {
        return JSON.parse(messages);
      } catch (e) {
        log.debug(`Real time update message parsing Error, MESSAGE - [${message}] ERROR - ${JSON.stringify(e)}`);
      }
    }
    return '';
  }

  private displayRealtimeWarningModal() {
    this.notify.decision(this.translate.instant('realTimeUpdatesUnavailable'), {
      headerContent: this.translate.instant('realTimeUnavailableTitle'),
      content: this.translate.instant('realTimeUpdatesUnavailable'),
      yesButtonLabel: this.translate.instant('label.OK'),
      isBlockModal: true,
      disableNoBtn: true,
      isDecisionModal: true,
      enableYesBtn: true
    });
  }

  private displayRealTimeConnectionLostThrottle = _.throttle(() => {
    this.notify.error(this.translate.instant('realtimeConnectionError'));
    log.error(`Real-Time Connection Lost`);
  }, 30000);

  private displayRealTimeConnectionSuccessThrottle = _.throttle(() => {
    this.displayRealTimeConnectionLostThrottle.cancel();
    this.notify.success(this.translate.instant('realtimeConnectionSuccess'));
    log.info(`Real-Time Connection Restored`);
  }, 30000);

  private publishStateEvents(response: any) {
    const broadcast =
      response.request.actorName !== this.appConfigService.getUserId() ||
      response.request.actionCode === Events.unassignAll.value;
    if (broadcast) {
      const topic = RealTimeService.actionCodeToChannelName(response.request.actionCode) + '.after.' + EventType.REMOTE;
      this.messageService.broadcast(topic, response);
    }
  }

  static isValidRMQNotificationMsgResponse(response) {
    return response && response.eventName === 'RMQNotificationMsg';
  }

  static isValidExamAvailabilityChangeMsgResponse(response) {
    return response && response.name === 'ExamAvailabilityStatusChange';
  }

  static isValidNotificationMsgResponse(response) {
    return response && response.eventName === 'notificationMsg';
  }

  static isValidExamFlagResponse(response) {
    return response && response.event === 'ExamFlagChange';
  }

  static isValidDicomStatusResponse(response) {
    return response && response.name === 'dicomSendStatus';
  }
  static isValidExamSplitResponse(response) {
    return response && (response.name === 'ExamSplitSeries' || response.event === 'ExamSplitSeries');
  }

  static isValidExamNoteResponse(response: any) {
    return (
      response.examNoteRequest &&
      response.examNoteRequest.action &&
      response.examNoteRequest.requester &&
      response.noteId
    );
  }

  static isValidExamTagResponse(response: any) {
    return response && response.name === 'examTagRequest' && response.examId;
  }

  static isValidDicomqrRetrieveResponse(response: any) {
    return response && response.name === 'DicomqrRetrieveUpdate';
  }

  static isValidPatientHistoryResponse(response: any) {
    return response && response.eventName === 'COMPARISONS';
  }

  static isValidStudySearchResponse(response: any) {
    return response && response.eventName === 'STUDY_SEARCH';
  }

  static isValidATNARepoCommnResponse(response: any) {
    return response && response.eventName === 'ATNARepoCommunicationStatus';
  }

  static isValidCacheUnStableResponse(response: any) {
    return response && response.eventName === 'CACHE_HEALTH_STATUS_UNSTABLE';
  }

  static isValidCacheUnhealthyStatusResponse(response: any) {
    return response && response.eventName === 'CACHE_HEALTH_STATUS_UNHEALTHY';
  }

  static isValidCacheHealthyStatusResponse(response: any) {
    return response && response.eventName === 'CACHE_HEALTH_STATUS_HEALTHY';
  }

  static isValidExamPriorityResponse(response: any) {
    return response && (response.eventName === 'ExamPriority' || response.name === 'ExamPriorityChange');
  }

  static isValidArchiveAdoptedResponse(response: any) {
    return response && response.name === 'archiveStatusChange';
  }

  static isValidCountResponse(response: any) {
    return response && response.name === 'WorklistCountChange';
  }

  static isValidWorkListResponse(response: any) {
    return response && response.name === 'Worklist' && response.action && response.rowId;
  }

  static isValidTakeControlAutoAckResponse(response: any) {
    return (
      response.action === 'lock-release-auto-response' &&
      response.name === 'takecontrol' &&
      response.status === 'autoAck'
    );
  }

  static isValidCacheRefreshResponse(response: any) {
    return (
      response &&
      (response.name === 'EXAM_PRIORITY_CACHE_REFRESH' || response.eventName === 'EXAM_PRIORITY_CACHE_REFRESH')
    );
  }

  static isValidUnrejectImageResponse(response: any) {
    return response && response.eventName === 'UnrejectAllImages';
  }

  static actionCodeToChannelName(actionCode) {
    const action = _.findWhere(_.values(Events), { value: actionCode });
    if (!action) {
      throw new InvalidArgumentException('Could not find RemoteAction with actionCode "' + actionCode + '"');
    }
    return action.name;
  }

  private handleExamPriorityUpdateMessage(message) {
    const changeEvent: 'ExamPriority' | 'ExamPriorityChange' = message['name'] || message['eventName'];
    let statValueObj = {};
    if (changeEvent === 'ExamPriorityChange') {
      const priorityCode = parseInt(message['exam_priority_code']);
      if (isNaN(priorityCode)) {
        log.error("Invalid 'exam_priority_code' in message" + message);
        return;
      }
      const priorityObj = this.searchFilterCriteriaService.getPriorityById(priorityCode);
      statValueObj = {
        statValue: priorityObj.priorityName,
        id: parseInt(message['exam_ckey']),
        priorityId: priorityCode,
        priorityBadgeColor: priorityObj.priorityBadgeColor
      };
    } else {
      const requestedProcedureId = parseInt(message.requestedProcedureId);
      statValueObj = {
        statValue: message.priority,
        id: requestedProcedureId,
        priorityId: Number(message.priorityId),
        priorityBadgeColor: UtilService.isNull(message.priorityBadgeColor) ? '' : message.priorityBadgeColor
      };
    }
    this.messageService.broadcast('statValueChanged', statValueObj);
  }
}
